import { useEffect } from 'react';
import { CheckIcon } from 'lucide-react';
import { convertSpacesToLatex, wrapDisplaylines, newlinesToLatexBreaks, removeDisplaylines } from '@/utils/preprocess';
import type { MCQOption } from '@/utils/mcq-utils';
import { installMathFieldWrapper } from '@/components/readonly-question-display';

// Declare the math-field element type
declare global {
  namespace JSX {
    interface IntrinsicElements {
      'math-field': any;
    }
  }
}

interface MCQAnswerEditorProps {
  options: MCQOption[];
  onOptionsChange: (options: MCQOption[]) => void;
  /** Which language's content to show in the math-field inputs */
  lang: 'zh-HK' | 'en';
  /** Whether to show the correct-answer checkboxes (hide for e.g. English section in bilingual mode) */
  showCheckbox?: boolean;
}

export function MCQAnswerEditor({ options, onOptionsChange, lang, showCheckbox = true }: MCQAnswerEditorProps) {
  useEffect(() => {
    installMathFieldWrapper();
  }, [options, lang]);

  const updateOption = (index: number, updates: Partial<MCQOption>) => {
    const newOptions = options.map((opt, i) =>
      i === index ? { ...opt, ...updates } : opt,
    );
    onOptionsChange(newOptions);
  };

  const isZh = lang === 'zh-HK';

  return (
    <div className="space-y-2">
      {options.map((option, index) => (
        <div key={option.label} className="flex items-center gap-3">
          <span className="text-base font-semibold text-primary w-6 text-right shrink-0">
            {option.label}.
          </span>
          <math-field
            className="flex-1 border-gray-200 border-2 rounded-md min-h-[40px]"
            onInput={(evt: any) => {
              const value = removeDisplaylines(evt.target.value);
              if (isZh) {
                updateOption(index, { content: value });
              } else {
                updateOption(index, { contentEn: value });
              }
            }}
          >
            {wrapDisplaylines(newlinesToLatexBreaks(convertSpacesToLatex(isZh ? option.content : option.contentEn)))}
          </math-field>
          {showCheckbox && (
            <button
              type="button"
              onClick={() => {
                const newOptions = options.map((opt, i) => ({
                  ...opt,
                  isCorrect: i === index ? !opt.isCorrect : false,
                }));
                onOptionsChange(newOptions);
              }}
              className={`shrink-0 flex items-center justify-center rounded-full size-6 cursor-pointer transition-colors ${
                option.isCorrect
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground border border-border'
              }`}
            >
              <CheckIcon className="size-4" />
            </button>
          )}
        </div>
      ))}
    </div>
  );
}
